import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useJob } from "../../services/api-client/useJob";
import { useJobHistory } from "../../services/api-client/useJobHistory";
import LongFormSkeleton from "../../components/DesignSystem/Skeletons/LongFormSkeleton";
import { StatusMenu } from "../../components/DesignSystem/Menu/StatusMenu";
import GoAwayButton from "../../components/DesignSystem/Input/GoAwayButton";
import FloatingGeneratingLabel from "../../components/DesignSystem/Output/FloatingGeneratingLabel";
import { Tabs, TabContent } from "../../components/DesignSystem/Tabs/Tabs";
import { DONE } from "../../DataModels/DataModels";
import TextYScroller from "../../components/DesignSystem/Output/textYScroller";
import EditableLabel from "../../components/DesignSystem/Input/EditableLabel";
import { stripMarkdown } from "../../Utilities/StripMarkdown";
import HistoryTimeline from "../../components/DesignSystem/Output/HistoryTimeline";
import NotesTab from "../../components/DesignSystem/Output/NotesTab";
import ContactsTab from "../../components/DesignSystem/Output/ContactsTab";
import { useCoverLetterDownload } from "../../components/CoverLetterPdf/useCoverLetterDownload";

const JobPage = () => {
	const { jobId } = useParams();
	const navigate = useNavigate();
	const activeJob = useJob(jobId ?? "0");
	const [historyVisible, setHistoryVisible] = useState(false);
	const { events: historyEvents, isLoading: historyIsLoading } = useJobHistory(
		jobId ?? "0",
		historyVisible
	);
	const { download: downloadCoverLetter } = useCoverLetterDownload({
		coverLetterMarkdown: activeJob?.coverLetter,
		companyName: activeJob?.company
	});

	const renderRoleTab = () => (
		<div className="py-6">
			<div className="">
				{activeJob?.isLoading ? (
					<LongFormSkeleton className="min-h-96" />
				) : (
					<TextYScroller
						tailClassMinHeight="min-h-0"
						tailClassMaxHeight="max-h-[calc(100vh-300px)]"
					>
						<pre className="whitespace-pre-wrap font-sans">
							{activeJob?.jobDescription}
						</pre>
					</TextYScroller>
				)}
			</div>
		</div>
	);

	const renderResumeTab = () => (
		<div className="space-y-4">
			<FloatingGeneratingLabel
				isLoading={activeJob?.isLoading ?? true}
				isGenerating={activeJob?.isGeneratingResume ?? false}
				onGenerate={() => activeJob?.generateResume()}
				contentLength={activeJob?.resume?.length ?? 0}
				generateLabel="Generate Resume"
			/>

			{activeJob?.isGeneratingResume || activeJob?.isLoading ? (
				<LongFormSkeleton className="min-h-96" />
			) : (
				<div>
					<TextYScroller
						tailClassMinHeight="min-h-0"
						tailClassMaxHeight="max-h-[calc(100vh-300px)]"
					>
						<pre className="whitespace-pre-wrap font-sans">
							{stripMarkdown(activeJob?.resume ?? "")}
						</pre>
					</TextYScroller>
				</div>
			)}
		</div>
	);

	const renderHistoryTab = () => (
		<div className="space-y-4">
			{historyIsLoading ? (
				<LongFormSkeleton className="min-h-96" />
			) : (
				<TextYScroller
					tailClassMinHeight="min-h-0"
					tailClassMaxHeight="max-h-[calc(100vh-300px)]"
				>
					<HistoryTimeline events={historyEvents} />
				</TextYScroller>
			)}
		</div>
	);

	const renderCoverLetterTab = () => (
		<div className="space-y-4">
			<FloatingGeneratingLabel
				isLoading={activeJob?.isLoading ?? true}
				isGenerating={activeJob?.isGeneratingCoverLetter ?? false}
				onGenerate={() => activeJob?.generateCoverLetter()}
				contentLength={activeJob?.coverLetter?.length ?? 0}
				generateLabel="Generate Cover Letter"
				onDownload={downloadCoverLetter}
			/>

			{activeJob?.isGeneratingCoverLetter || activeJob?.isLoading ? (
				<LongFormSkeleton className="min-h-96" />
			) : (
				<div>
					<TextYScroller
						tailClassMinHeight="min-h-0"
						tailClassMaxHeight="max-h-[calc(100vh-300px)]"
					>
						<pre className="whitespace-pre-wrap font-sans">
							{activeJob?.coverLetter}
						</pre>
					</TextYScroller>
				</div>
			)}
		</div>
	);

	return (
		<div className="flex justify-center">
			{/* Mobile and Desktop Container */}
			<div className="w-full md:w-[56rem] overflow-hidden">
				<div className="flex flex-row items-start justify-between text-primary pl-6 pr-6 mb-4">
					<div>
						<div className="flex flex-col items-start">
							{activeJob?.isLoading || !activeJob ? (
								<div className="animate-pulse space-y-4">
									<div className="animate-pulse">
										<div className="h-8 my-2 bg-primary-accent rounded w-64"></div>
										<div className="h-8 my-2 bg-primary-accent rounded w-32"></div>
									</div>
								</div>
							) : (
								<>
									<EditableLabel
										value={activeJob.company}
										onSubmit={(newValue) => {
											activeJob.updateJob({ company: newValue });
										}}
										className="text-xl my-2 h-8 font-bold max-w-[calc(100vw-70px)]"
										inputClassName="text-xl font-bold"
									/>
									<EditableLabel
										value={activeJob.title}
										onSubmit={(newValue) => {
											activeJob.updateJob({ title: newValue });
										}}
										className="text-lg my-2 h-8 max-w-[calc(100vw-70px)]"
										inputClassName="text-lg"
									/>
								</>
							)}
						</div>
					</div>
					<div>
						<GoAwayButton
							className="stroke-primary-inactive mt-2 hover:stroke-primary-active hover:fill-primary-active"
							onClick={() => navigate(-1)}
						/>
					</div>
				</div>
				{/* Tabs Section */}
				<div className="">
					<Tabs className="text-sm bg-primary-accent py-4 px-2">
						<TabContent title="Role" className="p-6">
							{renderRoleTab()}
						</TabContent>

						<TabContent title="Notes" className="p-6">
							<NotesTab jobId={jobId ?? "0"} />
						</TabContent>

						<TabContent title="Contacts" className="p-6">
							<ContactsTab jobId={jobId ?? "0"} />
						</TabContent>

						<TabContent title="Cover Letter" className="p-6">
							{renderCoverLetterTab()}
						</TabContent>

						<TabContent title="Resume" className="p-6">
							{renderResumeTab()}
						</TabContent>

						<TabContent
							title="History"
							className="p-6"
							onVisible={() => setHistoryVisible(true)}
						>
							{renderHistoryTab()}
						</TabContent>
					</Tabs>
				</div>
				{/* Floating Job State Button */}
				{!activeJob?.isLoading && activeJob && (
					<div className="fixed bottom-16 left-1/2 transform -translate-x-1/2 z-menu">
						<StatusMenu
							onClick={(obj: any) => {
								activeJob.updateJobState(obj.toState, obj.reason, obj.outcome);
							}}
							currentState={activeJob?.state ?? DONE}
							currentJobId={activeJob?.id}
						/>
					</div>
				)}{" "}
			</div>
		</div>
	);
};

export default JobPage;
